import React from 'react';
import { DateTime } from 'luxon';

// import components
import SolidImageCards from 'components/partials/solid-image-cards';

// import helpers
import { isGatsbyImageData } from "helpers/is-gatsby-image";
import { getLinkDataFromDato } from "helpers/get-link-data";

// import types
import { ImageContentCardData } from "types/image-content-card-data";

const CardsWithLabelsCollectionBuilder: React.FC<{ cardsWithLabelsCollectionData?: Queries.CardsWithLabelsCollectionBlockFragment }> = ({ cardsWithLabelsCollectionData }) => {

    if (!cardsWithLabelsCollectionData) return null;

    let headline = "Error: No headline provided.";

    if (cardsWithLabelsCollectionData.heading) {
        headline = cardsWithLabelsCollectionData.heading;
    }

    let useLargeHeadline = false;

    if (cardsWithLabelsCollectionData.headingSize === 'large') {
        useLargeHeadline = true;
    }

    const intro = {
        headline: headline,
        largeHeadline: useLargeHeadline,
    };

    let imageCardColumns = 3;

    if (cardsWithLabelsCollectionData.cardSize === 'large') {
        imageCardColumns = 2;
    }

    const cards: ImageContentCardData[] = cardsWithLabelsCollectionData.cards?.map((card): ImageContentCardData | undefined => {
        if (!card) return undefined;

        // provides with info for front-end url, button text, and button target
        // also provides with info for the card's title, description, and image
        let linkData = getLinkDataFromDato(card.link?.link);

        let cardHeadline = linkData?.title || 'Error: missing title';
        let cardSubtext = linkData?.shortDescription || '';
        if (card.headingOverride) cardHeadline = card.headingOverride;
        if (card.labelOverride) cardSubtext = card.labelOverride;

        let cardImage = linkData?.carouselCardImage || undefined;
        if (card.imageOverride && typeof card.imageOverride === 'object') {
            cardImage = isGatsbyImageData(card.imageOverride.gatsbyImageData) ? card.imageOverride.gatsbyImageData : undefined;
        }

        let cardImageAlt = card.imageOverride?.alt || '';

        let label = card.labelOverride || '';

        // if there's a linkData event start and end date, use that for the label
        if (linkData?.startDate || linkData?.endDate) {
            label = formatDateRange(linkData.startDate, linkData.endDate);
        }

        return {
            id: card.id,
            headline: cardHeadline,
            label: label,
            subtext: cardSubtext,
            cardImage: cardImage,
            cardImageAlt: cardImageAlt,
            buttonText: linkData?.label || 'Error: Missing label',
            buttonUrl: linkData?.url || '#',
            buttonUrlTarget: linkData?.target || '_self',
        };
    }).filter((card): card is ImageContentCardData => card !== undefined) || [];;

    return (
        <SolidImageCards headline={intro.headline} largeHeadline={intro.largeHeadline} cards={cards} columns={imageCardColumns} />
    );
};

export default CardsWithLabelsCollectionBuilder;

// Note: Because this range uses ISO date time strings (complete with timezone offset), we can stick with JS Date objects here
function formatDateRange(startDateTime?: string, endDateTime?: string): string {
    if (!startDateTime && !endDateTime) {
        return '';
    }

    const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
    const startDateFormat = new Intl.DateTimeFormat('en-US', options);
    const endDateFormat = new Intl.DateTimeFormat('en-US', options);

    if (startDateTime && !endDateTime) {
        return startDateFormat.format(new Date(startDateTime));
    }

    if (!startDateTime && endDateTime) {
        return endDateFormat.format(new Date(endDateTime));
    }

    // Ensure startDate and endDate are not undefined before creating Date objects
    const start = startDateTime ? new Date(startDateTime) : new Date();
    const end = endDateTime ? new Date(endDateTime) : new Date();

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${startDateFormat.format(start).split(' ')[0]} ${start.getDate()}-${end.getDate()} ${start.getFullYear()}`;
    } else {
        return `${startDateFormat.format(start)} - ${endDateFormat.format(end)}`;
    }
}